import React, { useEffect, useState } from 'react';
import { TextField, Button, Grid2, Alert, FormControl, Autocomplete, Checkbox, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportServices } from '../../services/reportServices';
import { useAuth } from '../../services/authentication/authContext';
import Clear from '@mui/icons-material/Clear';
import GetApp from '@mui/icons-material/GetApp';
import { userServices } from '../../services/userServices';
import { downloadReport } from '../../utils/downloadReport ';
import '../../css/Reports.css';
import ReportGenerateButton from '../common/ReportGenerateButton';
import PageHeader from '../ui/PageHeader';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';

const EmpJobTaskReport = ({ showHeader = true }) => {

    const [userList, setUserList] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState('');
    const [generating, setGenerating] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const { accessToken } = useAuth();

    useEffect(() => {
        if (accessToken) {
            setLoading(true);
            userServices.getUsersByRoleName()
                .then((response) => {
                    setUserList(response)
                    setLoading(false);
                })
                .catch((error) => {
                    setLoading(false);
                });
        }
    }, [accessToken]);

    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        setSelectedUserIds([]);
    };

    const handleReport = () => {
        setGenerating(true);
        const formData = {
            startDate,
            endDate,
            userIds: selectedUserIds
        };
        if (startDate <= endDate) {
            reportServices.downloadEmployeeJobTaskReport(formData)
                .then((response) => {

                    downloadReport(response);

                    setGenerating(false);
                    setSuccessMessage("Report Generated Successfully!");
                    setTimeout(() => setSuccessMessage(null), 4000);

                    setSelectedUserIds([]);
                    setEndDate('');
                    setStartDate('');
                })
                .catch((error) => {
                    setError(error.message);
                    setTimeout(() => {
                        setError(null);
                    }, 4000);
                    setGenerating(false);
                });
        } else {
            setError('Error! Start date must be earlier than end date.');
            setTimeout(() => {
                setError(null);
            }, 4000);
            setGenerating(false);
        }
    };

    return (
        <div className={showHeader ? "reports-container" : ""}>
            {showHeader && (
                <PageHeader title="Employee Day Wise Report" />
            )}
            <Box className={showHeader ? "report-box" : ""}>

                {successMessage && (
                    <Alert variant="filled" severity="success" style={{ marginBottom: '1.25rem' }}>
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert variant="outlined" severity="error" style={{ marginBottom: '1.25rem' }}>
                        {error}
                    </Alert>
                )}

                <form style={{ marginTop: '1.25rem' }}>
                    <Grid2 container spacing={3}>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <TextField
                                type="date"
                                label={t('report.startDate')}
                                variant="outlined"
                                fullWidth
                                required
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <TextField
                                type="date"
                                label={t('report.endDate')}
                                variant="outlined"
                                fullWidth
                                required
                                value={endDate}
                                disabled={startDate ? false : true}
                                onChange={(e) => setEndDate(e.target.value)}
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth margin="normal">
                                <Autocomplete
                                    multiple
                                    id="multi-user-checkbox"
                                    options={[{ id: 'all', firstName: 'Select All' }, ...userList]}
                                    disableCloseOnSelect
                                    value={userList.filter(user => selectedUserIds.includes(user.id))}
                                    getOptionLabel={(user) =>
                                        user.id === 'all'
                                            ? 'Select All'
                                            : `${user.firstName} ${user.middleName ?? ''} ${user.lastName}`
                                    }
                                    onChange={(event, newValue) => {
                                        const isSelectAllClicked = newValue.some(user => user.id === 'all');

                                        if (isSelectAllClicked) {
                                            // Add all users to selection (ignore duplicates)
                                            const allUserIds = userList.map(user => user.id);
                                            const isAllSelected = selectedUserIds.length === userList.length;

                                            if (isAllSelected) {
                                                setSelectedUserIds([]);
                                            } else {
                                                setSelectedUserIds(allUserIds);
                                            }
                                        } else {
                                            const filtered = newValue.filter(user => user.id !== 'all');
                                            setSelectedUserIds(filtered.map((user) => user.id));
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option, { selected }) => {
                                        const isAllSelected = selectedUserIds.length === userList.length;

                                        return (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    style={{ marginRight: 8 }}
                                                    checked={option.id === 'all' ? isAllSelected : selected}
                                                />
                                                {option.id === 'all'
                                                    ? 'Select All'
                                                    : `${option.firstName} ${option.middleName ?? ''} ${option.lastName}`}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="User"
                                        />
                                    )}
                                />

                            </FormControl>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }} className="gridContainer">
                            <ReportGenerateButton
                                onClick={handleReport}
                                loading={generating}
                                disabled={!(startDate && endDate && selectedUserIds.length > 0) || (Boolean(loading) || generating)}
                                className={`commonButton ${(startDate && endDate && selectedUserIds.length > 0) ? 'reportButtonSuccess' : 'reportButtonDefault'}`}
                                icon={<GetApp className="buttonIcon" />}
                                label={t('job.report')}
                                loadingLabel="Preparing your report..."
                            />
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleClear}
                                className={`commonButton clearButton`}
                                disabled={(Boolean(loading) || generating)}
                            >
                                <Clear className="buttonIcon" /> {t('job.clear')}
                            </Button>
                        </Grid2>

                    </Grid2>
                </form>
            </Box>
        </div>
    );
};

export default EmpJobTaskReport;